/**
 * MifeLife v2 — Single top navbar for public + dashboard surfaces.
 *
 * Premium dark, violet accent (#8B5CF6), cyan secondary (#06B6D4).
 * Shows: logo · main links · language switcher · auth state.
 */
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, ChevronDown, Shield } from 'lucide-react';
import { getToken } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';

const ADMIN_EMAILS = new Set(['hak.dalmis@gmail.com', 'demo_founder@mifelife.com']);
const isAdmin = (u) => !!u && (u.role === 'admin' || ADMIN_EMAILS.has((u.email || '').toLowerCase()));

const COPY = {
  tr: { analiz: 'Analiz', analizSelf: 'Kendim için', analizCo: 'Şirketim için', mentorler: 'Mentörler', mentorOl: 'Mentor Ol', fiyatlandirma: 'Fiyatlandırma', blog: 'Blog', giris: 'Giriş', kayit: 'Ücretsiz Başla', dashboard: 'Panele git', admin: 'Admin' },
  en: { analiz: 'Analysis', analizSelf: 'For myself', analizCo: 'For my company', mentorler: 'Mentors', mentorOl: 'Become a Mentor', fiyatlandirma: 'Pricing', blog: 'Blog', giris: 'Sign in', kayit: 'Start free', dashboard: 'Go to app', admin: 'Admin' },
  de: { analiz: 'Analyse', analizSelf: 'Für mich', analizCo: 'Für mein Unternehmen', mentorler: 'Mentoren', mentorOl: 'Mentor werden', fiyatlandirma: 'Preise', blog: 'Blog', giris: 'Anmelden', kayit: 'Kostenlos starten', dashboard: 'Zur App', admin: 'Admin' },
};

export default function V2Nav({ lang = 'tr', onLangChange }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();
  const T = COPY[lang] || COPY.tr;
  const authed = !!getToken();
  const showAdmin = authed && isAdmin(user);
  const isActive = (p) => loc.pathname === p || loc.pathname.startsWith(p + '/');

  const switchLang = (l) => {
    onLangChange ? onLangChange(l) : nav('/');
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#0F0F1A]/80 border-b border-white/[0.06]" data-testid="v2-nav">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2 group" data-testid="v2-nav-logo">
          <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-cyan-400 flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-white" strokeWidth={2.4} />
          </span>
          <span className="text-base font-semibold tracking-tight text-white">MifeLife</span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 ml-4">
          <AnalysisDropdown T={T} isActive={isActive('/analiz') || isActive('/sirket')} />
          <NavLink to="/mentorler" label={T.mentorler} active={isActive('/mentorler')} testid="v2-nav-mentorler" />
          <NavLink to="/mentor-basvuru" label={T.mentorOl} active={isActive('/mentor-basvuru')} testid="v2-nav-mentor-ol" />
          <NavLink to="/fiyatlandirma" label={T.fiyatlandirma} active={isActive('/fiyatlandirma')} testid="v2-nav-fiyatlandirma" />
          <NavLink to="/blog" label={T.blog} active={isActive('/blog')} testid="v2-nav-blog" />
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <LangSwitcher lang={lang} onSwitch={switchLang} />
          {authed ? (
            <>
              {showAdmin && (
                <Link
                  to="/admin"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-violet-400/30 bg-violet-500/10 text-violet-200 text-sm font-medium hover:bg-violet-500/20 transition"
                  data-testid="v2-nav-admin"
                >
                  <Shield className="w-3.5 h-3.5" strokeWidth={2} /> {T.admin}
                </Link>
              )}
              <Link to="/app" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-medium hover:from-violet-400 hover:to-violet-500 transition" data-testid="v2-nav-dashboard">
                {T.dashboard}
              </Link>
            </>
          ) : (
            <>
              <Link to="/giris" className="text-sm text-slate-300 hover:text-white transition" data-testid="v2-nav-giris">{T.giris}</Link>
              <Link to="/kayit" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-medium hover:from-violet-400 hover:to-violet-500 transition shadow-lg shadow-violet-500/20" data-testid="v2-nav-kayit">
                {T.kayit}
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

const NavLink = ({ to, label, active, testid }) => (
  <Link
    to={to}
    className={`text-[13px] font-medium transition ${active ? 'text-white' : 'text-slate-400 hover:text-slate-100'}`}
    data-testid={testid}
  >
    {label}
  </Link>
);

const AnalysisDropdown = ({ T, isActive }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid="v2-nav-analiz-dropdown"
    >
      <button
        type="button"
        className={`inline-flex items-center gap-1 text-[13px] font-medium transition ${isActive ? 'text-white' : 'text-slate-400 hover:text-slate-100'}`}
        data-testid="v2-nav-analiz"
      >
        {T.analiz}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 pt-3 z-50" data-testid="v2-nav-analiz-menu">
          <div className="w-60 rounded-xl border border-white/10 bg-[#0a0a14]/95 backdrop-blur-md shadow-xl py-1.5">
            <Link to="/analiz" className="flex flex-col px-4 py-2.5 hover:bg-white/[0.04] transition" data-testid="v2-nav-analiz-self">
              <span className="text-sm text-white font-medium">{T.analizSelf}</span>
              <span className="text-[11px] text-slate-500">10 soru · kişisel yol haritası</span>
            </Link>
            <Link to="/sirket" className="flex flex-col px-4 py-2.5 hover:bg-white/[0.04] transition" data-testid="v2-nav-analiz-company">
              <span className="text-sm text-white font-medium">{T.analizCo}</span>
              <span className="text-[11px] text-slate-500">8 soru · şirket sağlık taraması</span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

const LangSwitcher = ({ lang, onSwitch }) => (
  <div className="hidden sm:flex items-center gap-1 text-[11px] uppercase tracking-wider">
    {['tr', 'en', 'de'].map((l) => (
      <button
        key={l}
        onClick={() => onSwitch(l)}
        className={`px-1.5 transition ${lang === l ? 'text-white' : 'text-slate-500 hover:text-slate-300'}`}
        data-testid={`v2-lang-${l}`}
      >
        {l.toUpperCase()}
      </button>
    ))}
  </div>
);
